import InfoIcon from "@mui/icons-material/InfoOutline";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { AdminProfileFormData } from "validations/adminSchemas";

import GenericImageUpload from "../../common/GenericImageUpload";

function AdminInformationSection({ avatar }: { readonly avatar: string }) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<AdminProfileFormData>();

  // Memoize watched values to prevent unnecessary re-renders
  const watchedValues = watch();
  const { firstName, middleName, lastName } = useMemo(
    () => ({
      firstName: watchedValues.firstName,
      middleName: watchedValues.middleName,
      lastName: watchedValues.lastName,
    }),
    [watchedValues.firstName, watchedValues.middleName, watchedValues.lastName],
  );

  return (
    <Box className="space-y-6">
      <Box className="flex items-center justify-center gap-2">
        <GenericImageUpload<AdminProfileFormData>
          name="avatar"
          label="Upload Profile Picture"
          showButton={false}
          defaultImage={avatar}
        />
      </Box>
      <Typography
        variant="h6"
        className="text-2xl !mt-2 font-medium text-center"
      >
        {`${firstName || ""} ${middleName || ""} ${lastName || ""}`.trim()}
      </Typography>
      <Box className="flex items-center gap-2">
        <Typography variant="h6" className="text-xl font-medium">
          Admin Information{" "}
        </Typography>
        <Tooltip
          title="If you want to change your account email, please contact the technical team for further support."
          arrow
          placement="right"
        >
          <IconButton
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <InfoIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box className="space-y-4">
        {/* Account ID */}
        <Controller
          name="id"
          control={control}
          render={({ field }) => (
            <TextField
              id="id"
              label="Account ID"
              variant="outlined"
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              {...field}
            />
          )}
        />

        {/* Account Email */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Box className="relative">
              <TextField
                id="email"
                label="Account Email"
                variant="outlined"
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                {...field}
              />
            </Box>
          )}
        />

        <Box className="grid grid-cols-3 gap-4">
          {/* First Name */}
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                id="admin-first-name"
                label="First Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          {/* Middle Name */}
          <Controller
            name="middleName"
            control={control}
            render={({ field }) => (
              <TextField
                id="admin-middle-name"
                label="Middle Name"
                variant="outlined"
                fullWidth
                error={!!errors.middleName}
                helperText={errors.middleName?.message}
                // @ts-ignore - inputProps deprecated but maxLength not available in slotProps yet
                inputProps={{ maxLength: 64 }}
                {...field}
              />
            )}
          />

          {/* Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                id="admin-last-name"
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                // @ts-ignore - inputProps deprecated but maxLength not available in slotProps yet
                inputProps={{ maxLength: 64 }}
                {...field}
              />
            )}
          />
        </Box>

        {/* Phone Number */}
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              id="admin-phone-number"
              label="Phone Number"
              variant="outlined"
              fullWidth
              error={!!errors.phone}
              helperText={errors.phone?.message}
              {...field}
            />
          )}
        />
      </Box>
    </Box>
  );
}

export default AdminInformationSection;
